import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AwaitModules, ComponentSync, LL_V_L, TS_CollapsableContainerV2 } from '@nu-art/thunderstorm-frontend';
import './Component_Protocols.scss';
import { ProtocolData } from '../../../../../../_utils/ProtocolData/ProtocolData.js';
import { ModuleFE_Advisor_Action, ModuleFE_Advisor_ColorGroup, ModuleFE_Advisor_DiseaseProfile, ModuleFE_Advisor_OrderGroup, ModuleFE_Advisor_Pathway, ModuleFE_Advisor_PathwayState, ModuleFE_Advisor_PathwayTransition, ModuleFE_Advisor_Variable, ModuleFE_AdvisorState, ModuleFE_RuntimeActiveNavPathway, ModuleFE_RuntimeActiveNavState, ModuleFE_RuntimeFinding, ModuleFE_RuntimeOrder, ModuleFE_RuntimeProtocol } from '@app/advisor-frontend';
import { exists, filterInstances, voidFunction } from '@nu-art/ts-common';
import { ProtocolListAssignment } from '@app/advisor-shared';
import { advisorConfig_ShowOrderSetProtocols } from '../../../../../../modules/ModuleFE_AdvisorSettings/types.js';
import { ICONS } from '@app/styles';
import { ModuleFE_ProvideInfo } from '../../../../../../modules/provide-info/ModuleFE_ProvideInfo.js';
import { Component_AdvisorSL_Protocols } from '../../../../../loaders/skeleton/protocols/Component_AdvisorSL_Protocols.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../../../_analytics/consts.js';
import { Component_Protocol } from '../../../../protocol/Component_Protocol/Component_Protocol.js';
const modulesToAwait = [
    //Static Modules
    ModuleFE_Advisor_Pathway,
    ModuleFE_Advisor_PathwayState,
    ModuleFE_Advisor_PathwayTransition,
    ModuleFE_Advisor_DiseaseProfile,
    ModuleFE_Advisor_ColorGroup,
    ModuleFE_Advisor_Action,
    ModuleFE_Advisor_OrderGroup,
    ModuleFE_Advisor_Variable,
    //Runtime Modules
    ModuleFE_RuntimeProtocol,
    ModuleFE_RuntimeOrder,
    ModuleFE_RuntimeActiveNavPathway,
    ModuleFE_RuntimeActiveNavState,
    ModuleFE_RuntimeFinding,
];
class Component_Protocols_Class extends ComponentSync {
    //######################### Life Cycle #########################
    __onProvideInfoContextUpdate = voidFunction;
    __onProvideInfoContextChange = () => {
        const baseProtocolsContext = ModuleFE_ProvideInfo.getCurrentProtocolContextData();
        this.setState({ selectedProtocolId: baseProtocolsContext?.protocolData.protocol._id });
    };
    //Re-calculate on any change in these collections, as they all affect the actions for each protocol
    __onRuntimeActiveNavStateUpdated = () => this.reDeriveState();
    __onRuntimeProtocolsUpdated = () => this.reDeriveState();
    __onRuntimeFindingsUpdated = () => this.reDeriveState();
    __onRuntimeOrdersUpdated = () => this.reDeriveState();
    __onAdvisorSettingsUpdated(key) {
        if (key !== advisorConfig_ShowOrderSetProtocols)
            return;
        this.reDeriveState();
    }
    deriveStateFromProps(nextProps, state) {
        const allProtocols = filterInstances(ModuleFE_RuntimeProtocol.cache
            .all()
            .map(p => ProtocolData.createNew(p._id)));
        state.protocols = ProtocolData.filterDisplayable(ProtocolData.sort(allProtocols));
        return state;
    }
    componentDidMount() {
        ModuleFE_AdvisorAnalytics.trigger.uiLoad.uiLoad_protocols({
            elapsed_ms: ModuleFE_AdvisorAnalytics.getTimeSinceLoad()
        });
        this.checkSetNotCollapse();
    }
    componentDidUpdate() {
        this.checkSetNotCollapse();
    }
    //######################### Logic #########################
    checkSetNotCollapse = () => {
        //Set not collapsed and report to analytics if collapse state is undetermined and we have protocols
        if (exists(ModuleFE_AdvisorState.getStateProperty.collapsing.homePanel.ddx()))
            return;
        if (!this.state.protocols.length)
            return;
        ModuleFE_AdvisorState.setProperty.collapsing.homePanel.ddx(false);
        ModuleFE_AdvisorAnalytics.trigger.view.view__keyDDX({ method: 'programmatic' });
    };
    getProtocols_General = () => {
        return this.state.protocols.filter(p => p.getListAssignment() === ProtocolListAssignment.CONSIDER_NOW);
    };
    getProtocols_Ended = () => {
        return this.state.protocols.filter(p => p.getListAssignment() === ProtocolListAssignment.ENDED);
    };
    getProtocols_LC = () => {
        return this.state.protocols.filter(p => p.getListAssignment() === ProtocolListAssignment.CONSIDER_LATER);
    };
    getProtocols_Executed = () => {
        return this.state.protocols.filter(p => p.getListAssignment() === ProtocolListAssignment.EXECUTED);
    };
    //######################### Render #########################
    render() {
        if (!this.state.protocols.length)
            return this.render_NoProtocols();
        return _jsxs(LL_V_L, { className: 'c-protocols', children: [this.render_General(), this.render_LowConsideration(), this.render_Ended(), this.render_Executed()] });
    }
    render_NoProtocols = () => {
        return _jsx("div", { className: 'c-protocols message', children: "We currently do not have any relevant protocols to display" });
    };
    render_General = () => {
        const protocols = this.getProtocols_General();
        if (!protocols.length)
            return;
        return _jsx(LL_V_L, { className: 'c-protocols__general', children: protocols.map((p, rankIndex) => {
                const key = p.protocol._id + p.protocol.__updated;
                const selected = p.protocol._id === this.state.selectedProtocolId;
                return _jsx(Component_Protocol, { protocolData: p, selected: selected, rankIndex: rankIndex }, key);
            }) });
    };
    render_Ended = () => {
        const protocols = this.getProtocols_Ended();
        if (!protocols.length)
            return;
        return _jsx(TS_CollapsableContainerV2, { className: 'c-protocols__ended', headerRenderer: () => this.render_CC_Header('Ended', 'View protocols that have reached a final conclusion', protocols.length), containerRenderer: () => this.render_CC_Body(protocols), customCaret: _jsx(ICONS.treeCollapse.component, {}), forceUpdate: true, animated: true });
    };
    render_LowConsideration = () => {
        const protocols = this.getProtocols_LC();
        if (!protocols.length)
            return;
        return _jsx(TS_CollapsableContainerV2, { className: 'c-protocols__lc', headerRenderer: () => this.render_CC_Header('Low Consideration', 'View protocols that are low priority', protocols.length), containerRenderer: () => this.render_CC_Body(protocols), customCaret: _jsx(ICONS.treeCollapse.component, {}), forceUpdate: true, animated: true });
    };
    render_Executed = () => {
        const protocols = this.getProtocols_Executed();
        if (!protocols.length)
            return;
        return _jsx(TS_CollapsableContainerV2, { className: 'c-protocols__executed', headerRenderer: () => this.render_CC_Header('Executed', 'View order sets that have been completed', protocols.length), containerRenderer: () => this.render_CC_Body(protocols), customCaret: _jsx(ICONS.treeCollapse.component, {}), forceUpdate: true, animated: true });
    };
    //######################### Render - Collapsable Container Parts #########################
    render_CC_Header = (title, subtitle, count) => {
        return _jsxs(LL_V_L, { className: 'c-protocols__group-header', children: [_jsxs("div", { className: 'c-protocols__group-header__title', children: [title, _jsx("div", { children: count })] }), _jsx("div", { className: 'c-protocols__group-header__sub-title', children: subtitle })] });
    };
    render_CC_Body = (protocols) => {
        return _jsx(LL_V_L, { className: 'c-protocols__group-body', children: protocols.map((p, rankIndex) => {
                const key = p.protocol._id + p.protocol.__updated;
                const selected = p.protocol._id === this.state.selectedProtocolId;
                return _jsx(Component_Protocol, { protocolData: p, selected: selected, rankIndex: rankIndex }, key);
            }) });
    };
}
export const Component_Protocols = (p) => _jsx(AwaitModules, { modules: modulesToAwait, Component: () => _jsx(Component_Protocols_Class, { ...p }), customLoader: _jsx(Component_AdvisorSL_Protocols, {}) });
//# sourceMappingURL=Component_Protocols.js.map